import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import boardInviteService from '../../services/boardInvite';
import { BoardInvite } from '../../interfaces/boardInvite';
import { useAuth } from '../../contexts/AuthContext';

const AcceptBoardInvite: React.FC = () => {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invite, setInvite] = useState<BoardInvite | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!inviteCode) {
      setError('Código de convite inválido');
      setLoading(false);
      return;
    }

    loadInvite();
  }, [inviteCode]);

  const loadInvite = async () => {
    try {
      const inviteData = await boardInviteService.getInviteByCode(inviteCode!);
      setInvite(inviteData);

      // Check if user is logged in and email matches
      if (user && user.email !== inviteData.email) {
        setError('Este convite foi enviado para outro email. Faça login com o email correto.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Convite não encontrado ou inválido');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async () => {
    if (!user) {
      // Redirect to login with return URL
      navigate(`/login?returnUrl=/invite/board/${inviteCode}`);
      return;
    }

    setAccepting(true);
    setError(null);

    try {
      await boardInviteService.acceptInvite({ inviteCode: inviteCode! });
      setSuccess(true);
      
      // Redirect to board after 3 seconds
      setTimeout(() => {
        navigate(`/project/${invite?.boardId}/board/${invite?.boardId}`);
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao aceitar convite');
    } finally {
      setAccepting(false);
    }
  };

  const getAccessLevelColor = (level: string) => {
    switch (level) {
      case 'Administrator':
        return 'error';
      case 'Member':
        return 'primary';
      case 'Observer':
        return 'default';
      default:
        return 'default';
    }
  };

  const getAccessLevelDescription = (level: string) => {
    switch (level) {
      case 'Administrator':
        return 'Você poderá gerenciar o board, convidar usuários e modificar configurações.';
      case 'Member':
        return 'Você poderá criar e editar cards, colunas e colaborar no board.';
      case 'Observer':
        return 'Você poderá visualizar o board, mas não poderá fazer alterações.';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !invite) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CancelIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Convite Inválido
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')}>
            Voltar ao Início
          </Button>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Convite Aceito!
          </Typography>
          <Typography color="text.secondary">
            Você agora tem acesso ao board. Redirecionando...
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Convite para Board
        </Typography>

        {invite && (
          <Card sx={{ mt: 3, backgroundColor: 'background.default' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {invite.boardTitle}
              </Typography>
              
              <Box sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Convidado por: <strong>{invite.invitedByName}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Para: <strong>{invite.email}</strong>
                </Typography>
              </Box>

              <Box sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Nível de Acesso:
                </Typography>
                <Chip
                  label={invite.accessLevel}
                  color={getAccessLevelColor(invite.accessLevel)}
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {getAccessLevelDescription(invite.accessLevel)}
                </Typography>
              </Box>

              {!invite.isValid && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {invite.isUsed
                    ? 'Este convite já foi utilizado.'
                    : 'Este convite expirou.'}
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
          {invite?.isValid && (
            <>
              {!user ? (
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleAcceptInvite}
                >
                  Fazer Login para Aceitar
                </Button>
              ) : user.email === invite.email ? (
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleAcceptInvite}
                  disabled={accepting}
                >
                  {accepting ? <CircularProgress size={24} /> : 'Aceitar Convite'}
                </Button>
              ) : null}
            </>
          )}
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/')}
          >
            Cancelar
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default AcceptBoardInvite;
